import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { getUserPermission, UserKeys } from '../../../../../../api/users-api';
import { Page, SharedDriveModel } from '../../../../../../../types';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import SharedDrivesRenameDialog from './shared-drives-rename-dialog';
import SharedDrivesDeleteDialog from './shared-drives-delete-dialog';
import SharedDrivesSettingsDialog from './shared-drives-settings-dialog';
import SharedDrivesPermissionDialog from './shared-drives-permission-dialog';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { handleErrors } from '../../../../../../utils/errors';
import SharedDrivesCreateDialog from './shared-drives-create-dialog';
import SharedErrorBoundary from '../../../../../../shared/shared-error-boundary';
import SharedDrivesPaginatedGridToolbar from './shared-drive-paginated-grid-toolbar';
import { createGridOverlays } from '../../../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../../../../shared/shared-data-grid-server';
import ChangeLogRequiredOverlay from '../../../../../grid-overlay/change-log-required-overlay';
import { useToolbar } from '../../../../../../shared/shared-data-grid-toolbar-helper';
import { useCopySharedDriveId } from '../../../../../drive/shared-drives/use-copy-shared-drive-id';

import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddToDrive from '@mui/icons-material/AddToDrive';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Link from '@mui/icons-material/Link';
import Settings from '@mui/icons-material/Settings';
import Share from '@mui/icons-material/Share';

interface SharedDrivesPaginatedGridProps {
  userEmail: string;
  primaryDomain: string;
  driveRoles: string[];
  changeLogRoles: string[];
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  sharedDrives: Page<SharedDriveModel[]> | undefined;
  isLoading: boolean;
  paginationModel: {
    pageSize: number;
    page: number;
    cursor: string | null;
    search: string;
    driveId: string;
  };
  setPaginationModel: Dispatch<
    SetStateAction<{
      pageSize: number;
      page: number;
      cursor: string | null;
      search: string;
      driveId: string;
    }>
  >;
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  selectedDrive: SharedDriveModel | null;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  permissionCheckLoading: boolean;
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
  showChangeLogOverlay?: boolean;
  onOpenChangeLogDialog?: () => void;
}

export default function SharedDrivesPaginatedGrid({
  userEmail,
  primaryDomain,
  driveRoles,
  changeLogRoles,
  setSelectedFolder,
  sharedDrives: data,
  isLoading,
  paginationModel,
  setPaginationModel,
  setOpenPermissionCheckDialog,
  selectedDrive,
  setSelectedDrive,
  permissionCheckLoading,
  setPermissionCheckLoading,
  showChangeLogOverlay = false,
  onOpenChangeLogDialog,
}: SharedDrivesPaginatedGridProps) {
  const queryClient = useQueryClient();

  const { setSnackbar } = useStore();
  const [openCreateSharedDriveDialog, setOpenCreateSharedDriveDialog] =
    useState(false);
  const [openSharedDrivesRenameDialog, setOpenSharedDrivesRenameDialog] =
    useState(false);
  const [openDeleteSharedDrivesDialog, setOpenDeleteSharedDrivesDialog] =
    useState(false);
  const [openSharedDrivesSettingsDialog, setOpenSharedDrivesSettingsDialog] =
    useState(false);
  const [
    openSharedDrivesPermissionsDialog,
    setOpenSharedDrivesPermissionsDialog,
  ] = useState(false);
  const { handleCopySharedDriveId, CopyChangeLogDialog } = useCopySharedDriveId(
    {
      changeLogRoles,
    }
  );

  const columns: GridColDef<SharedDriveModel>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        display: 'flex',
        pinnable: false,
        renderCell: (params) => {
          const avatar = (
            <Tooltip title={params.row.name}>
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Avatar
                  src={params.row.backgroundImageLink}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2">{params.row.name}</Typography>
              </Stack>
            </Tooltip>
          );
          return avatar;
        },
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 150,
        type: 'date',
        display: 'flex',
        pinnable: false,
        valueGetter: (_value, row) => {
          if (row.createdDate === 0) return '';
          return new Date(row.createdDate);
        },
        valueFormatter: (value) => {
          if (value === '') return '';
          return new Date(value as string).toLocaleDateString();
        },
      },
      {
        field: 'id',
        headerName: 'Shared Drive ID',
        display: 'flex',
        flex: 0.4,
        pinnable: false,
        renderCell: (params) => {
          const avatar = (
            <Stack flexDirection="row" alignItems="center">
              <Tooltip arrow title="Copy">
                <span>
                  <IconButton
                    disabled={
                      !isAbleToSeeUIElement(
                        driveRoles,
                        DriveRoles.CanCopyShareDriveID.value
                      )
                    }
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      handleCopySharedDriveId(params.row);
                    }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={params.row.id}>
                <Typography variant="body2">{params.row.id}</Typography>
              </Tooltip>
            </Stack>
          );
          return avatar;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params: GridRowParams) => {
          const buttonArray = [];

          buttonArray.push(
            <GridActionsCellItem
              label={'Share'}
              icon={<Share />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesPermissionsDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanUpdateSharedDrivePermissions.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Settings'}
              icon={<Settings />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesSettingsDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanUpdateSharedDriveSettings.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Rename'}
              icon={<DriveFileRenameOutline />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenSharedDrivesRenameDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanRenameSharedDrives.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Delete'}
              icon={<Delete />}
              onClick={() => {
                setSelectedDrive(params.row);
                setOpenDeleteSharedDrivesDialog(true);
              }}
              showInMenu
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanDeleteSharedDrives.value
                )
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              label={'Link'}
              component={'a'}
              icon={
                <Tooltip title="Link" arrow>
                  <Link />
                </Tooltip>
              }
              // @ts-expect-error - href is valid when using component prop
              href={`https://drive.google.com/drive/folders/${params.row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          );

          return buttonArray;
        },
      },
    ],
    [driveRoles, setSelectedDrive, setSnackbar]
  );

  const toolbarProps = useMemo(
    () => ({
      paginationModel,
      driveRoles,
      setOpenCreateSharedDriveDialog,
    }),
    [driveRoles, paginationModel]
  );

  const overlays = showChangeLogOverlay
    ? {
        noRowsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={() => onOpenChangeLogDialog?.()}
            permissionName="View Shared Drives"
          />
        ),
        noResultsOverlay: () => (
          <ChangeLogRequiredOverlay
            onOpenDialog={() => onOpenChangeLogDialog?.()}
            permissionName="View Shared Drives"
          />
        ),
      }
    : createGridOverlays('Drives', <AddToDrive />);

  return (
    <>
      <SharedDataGridServer
        data={showChangeLogOverlay ? undefined : data}
        slots={{
          toolbar: useToolbar(SharedDrivesPaginatedGridToolbar, toolbarProps),
          ...overlays,
        }}
        rows={showChangeLogOverlay ? [] : undefined}
        onRowClick={async (params) => {
          try {
            setPermissionCheckLoading(true);

            const hasPermission = await queryClient.fetchQuery({
              queryKey: [
                UserKeys.SharedDriveUserPermission,
                userEmail,
                params.row.id,
              ],
              queryFn: () => getUserPermission(params.row.id, userEmail, true),
              retry: false,
              staleTime: 1000 * 60 * 2,
            });

            if (!hasPermission) {
              setSelectedDrive(params.row);
              setOpenPermissionCheckDialog(true);
            } else {
              setSelectedFolder(params.row.id);
            }
          } catch (e) {
            handleErrors({
              error: e,
              snackbarMessage: `There was an issue checking your permissions. Please try again.`,
            });
          } finally {
            setPermissionCheckLoading(false);
          }
        }}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              search: paginationModel.search,
              driveId: paginationModel.driveId,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: data?.cursor || '',
              search: paginationModel.search,
              driveId: paginationModel.driveId,
            });
          }
        }}
        onFilterModelChange={(model) => {
          if (isLoading) return;

          setPaginationModel({
            ...paginationModel,
            search: model.quickFilterValues?.[0] || '',
          });
        }}
        sx={{
          '.MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={!showChangeLogOverlay && (isLoading || permissionCheckLoading)}
        disableRowSelectionOnClick
      />
      {openCreateSharedDriveDialog ? (
        <SharedErrorBoundary section="Shared Drives Create Dialog">
          <SharedDrivesCreateDialog
            open={openCreateSharedDriveDialog}
            handleClose={() => {
              setOpenCreateSharedDriveDialog(false);
            }}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesRenameDialog ? (
        <SharedErrorBoundary section="Shared Drives Rename Dialog">
          <SharedDrivesRenameDialog
            open={openSharedDrivesRenameDialog}
            handleClose={() => {
              setOpenSharedDrivesRenameDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openDeleteSharedDrivesDialog ? (
        <SharedErrorBoundary section="Shared Drives Delete Dialog">
          <SharedDrivesDeleteDialog
            open={openDeleteSharedDrivesDialog}
            handleClose={() => {
              setOpenDeleteSharedDrivesDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesSettingsDialog ? (
        <SharedErrorBoundary section="Shared Drives Settings Dialog">
          <SharedDrivesSettingsDialog
            open={openSharedDrivesSettingsDialog}
            handleClose={() => {
              setOpenSharedDrivesSettingsDialog(false);
            }}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            primaryDomain={primaryDomain}
          />
        </SharedErrorBoundary>
      ) : null}
      {selectedDrive && data && openSharedDrivesPermissionsDialog ? (
        <SharedErrorBoundary section="Shared Drives Permissions Dialog (in shared-drives-grid)">
          <SharedDrivesPermissionDialog
            open={openSharedDrivesPermissionsDialog}
            handleClose={() => {
              setOpenSharedDrivesPermissionsDialog(false);
            }}
            userEmail={userEmail}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            changeLogRoles={changeLogRoles}
            driveRoles={driveRoles}
          />
        </SharedErrorBoundary>
      ) : null}
      {CopyChangeLogDialog}
    </>
  );
}
